import { THosStatus } from "hos-tools";
import DutyStatusLog, { NODEVICEID } from "../model/DutyStatusLog";
import { DriveStore } from "../store/reducers";
import { getCurrentVehicle } from "../utils/device";
import { UserService } from "../utils/users";
import TranslationService from "./translation";
import { Dialog } from "../ui/framework/dialog";
import { handleApi } from "../utils/error";

// Statuses this applies to as per CANELD 4.5.1 (step 2)
// Login/Logoff is omitted from showing the prompt as they are made automatically during login/logout
// But odometer/engine hours will still be hidden for them
// CycleOne/CycleTwo are special cases as a pseudo log is used for now
export const STATUSES_TO_SHOW_PROMPT: THosStatus[] = [
    "D", "ON", "SB", "OFF", "PC", "YM", "PC_Exempted", "WT",
    "Certify",
    "ExemptionOffDutyDeferral",
    "CanadaCycleOne", "CanadaCycleTwo"
];

export const STATUSES_TO_OMIT_STATUSDATA: THosStatus[] = [
    "Login", "Logoff",
    ...STATUSES_TO_SHOW_PROMPT
];

export default class CreateLogWithOmittedDataWarningService {
    private readonly dialogId = "warn-log-created-without-device-communication-dialog";
    private readonly dialogHeading = "Engine data unavailable";
    private readonly dialogBodyText =
        `<div>
            This event will be recorded without the following data:
            <ul>
                <li>Total vehicle distance (odometer)</li>
                <li>Total engine hours</li>
            </ul>
            This occurs when no vehicle is assigned or the ELD is not communicating.
        </div>`;
    private readonly buttonText = "Confirm";
    private readonly showCooldownMs = 60_000;

    private dialog: Dialog | null = null;
    private lastShownAt: number | null = null;

    constructor(
        private readonly userService: UserService,
        private readonly translatorService: TranslationService,
        private readonly store: DriveStore,
        private readonly currentUser: IUser | null
    ) {}

    /**
     * Shows the user a warning if certain conditions are met.
     * @param log - The log that was added that triggered this warning check.
     */
    showLogCreatedWithOmittedDataWarning(log: DutyStatusLog) {
        if (!this.shouldOpen(log)) {
            return;
        }

        this.dialog = new Dialog(
            this.dialogId,
            this.dialogBodyText,
            this.translatorService.getTranslator(),
            this.dialogHeading,
            [
                [{
                    text: this.buttonText,
                    id: "confirm-log-created-without-device-communication-warning",
                    spinner: true,
                    callback: () => this.dialog?.remove()
                }]
            ],
            {
                isNightMode: this.store.getState().data.nightMode,
                persistent: true,
                intervenient: true
            }
        );
        this.lastShownAt = Date.now();
        this.dialog.open().catch(handleApi);
    }

    /**
     * Determines whether the warning should be shown to the user. All of the following must be true.
     *
     * 1. The prompt is not already open.
     * 2. The dialog has not been shown within the last 60 seconds.
     * 3. Driver is using the Canadian ELD
     * 4. The log is one of the following statuses in STATUSES_TO_SHOW_PROMPT
     * 5. Log is being added for the current UI user
     * 6. Log is of a manual origin (not "Unassigned" or "Automatic")
     * 7. Log is for NoVehicle
     *      OR log matches the current device and the device is currently not communicating.
     *      In this case, the log must be made after the last record of communication (or if no record exists)
     * @param log - The duty status log used to validate the conditions.
     * @returns `true` if all conditions are met and the dialog can be opened, `false` otherwise.
     */
    private shouldOpen(log: DutyStatusLog): boolean {
        const isDialogAlreadyOpen = document.getElementById(this.dialogId);
        const isWithinCooldown = this.lastShownAt !== null && Date.now() - this.lastShownAt < this.showCooldownMs;
        const isUsingCanEld = this.store.getState().data.ELDJurisdiction === "CANELD";
        const isLogDriverCurrentUiUser = log.driver.id === this.currentUser?.id && this.userService.isDriverUsingUI(this.currentUser.name);
        const isAutomaticOrigin = log.origin === "Unassigned" || log.origin === "Automatic";

        if (
            isDialogAlreadyOpen
            || isWithinCooldown
            || !isUsingCanEld
            || STATUSES_TO_SHOW_PROMPT.indexOf(log.status) === -1
            || !isLogDriverCurrentUiUser
            || isAutomaticOrigin
        ) {
            return false;
        }

        if (log.device.id === NODEVICEID) {
            return true;
        }

        const lastDeviceCommunicationDateTime = this.store.getState().mobile.lastDeviceCommunicationDateTime;
        return log.device.id === getCurrentVehicle()?.id && this.store.getState().mobile.deviceCommunicating === false
            && (!lastDeviceCommunicationDateTime || new Date(lastDeviceCommunicationDateTime).getTime() < new Date(log.dateTime).getTime());
    }
}
